import { useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Pressable, Linking } from 'react-native';
import { PaymentConfig, Rates } from '~/api-services/PaymentConverterService/types';
import { Button, DateField, dateFormatHint, ErrorMessage, FormField, Select, Text } from '~/components/core';
import { FONT_SIZE_L, FONT_SIZE_M, FONT_SIZE_S, FONT_SIZE_XS } from '~/constants';
import { scaleSize } from '~/constants/scale';
import { useAppSelector, useStyles } from '~/hooks';
import { useAddFunds } from '~/hooks/useAddFunds';
import { useRates } from '~/hooks/useRates';
import i18n from '~/i18n';
import { formatNumber } from '~/lib/format';
import { accountBalanceSelector, appSettingsSelector } from '~/state/selectors';
import { Theme } from '~/theme';
import CopyRight from '../WithdrawFunds/CopyRight';
import config from '~/config';
import Checkbox from '~/components/core/Checkbox';
import OrderConvertService from '~/api-services/OrderConvertService';
import Loader from '~/components/core/Loader';
import { FontAwesome } from '~/components/core/Icons';
import { usePaymentProvider1DirectCardData } from '~/hooks/usePaymentProvider1DirectCardData';
import { auStates, caStates, countries, usState } from '~/constants/global';
import BillingFieldComponent from '~/components/features/AddFundsCardPaymentProvider1/BillingFieldComponent';
import { PaymentProvider1BillingField } from '~/api-services/CustomerInfoService/types';
import { formatDateServer, parseDateServer, validateEmail } from '~/lib/utils';
import FlowScreen from '../AddFundsShared/FlowScreen';

type Props = {
    paymentProvider1Method?: string;
    currentAmount?: string;
    setActiveRightTab?: Function;
    paymentProvider1Currency?: string;
};

type Field = {
    name: string;
    value: string;
};

export default function AddFundsCardPaymentProvider1({
    paymentProvider1Method,
    currentAmount,
    setActiveRightTab,
    paymentProvider1Currency,
}: Props) {
    const [redirectUrl, setRedirectUrl] = useState('');
    const [redirectFields, setRedirectFields] = useState<Field[]>([]);
    const route = useRoute();
    const selectedProvider: string = 'PAYMENT_PROVIDER_1';
    const selectedPaymentProvider1Method: string = paymentProvider1Method
        ? paymentProvider1Method
        : (route.params as any).paymentProvider1Method;
    const selectedPaymentProvider1Currency: string = paymentProvider1Currency
        ? paymentProvider1Currency
        : (route.params as any).currency;
    const [selectedRate, setSelectedRate] = useState<Rates>({} as Rates);
    const { ratesAndCurrencies } = useRates('CARD', 'PAYMENT_PROVIDER_1');
    const [cardPaymentConfigData, setCardPaymentConfigData] = useState<PaymentConfig | undefined>(undefined);
    const styles = useStyles(stylesGenerator);
    const currency = useAppSelector(accountBalanceSelector)?.currency || '';
    const [amount] = useState(currentAmount ? currentAmount : (route.params as any).amount);
    const { configData } = useAddFunds(amount != '' ? Number(amount) : 0);
    const [confirm, setConfirm] = useState(false);
    const [showAmountValidationMessage, setShowAmountValidationMessage] = useState(false);
    const [paymentResponseErrorMessage, setPaymentResponseErrorMessage] = useState('');
    const [showMinAmountValidationMessage, setShowMinAmountValidationMessage] = useState(false);
    const [showMaxAmountValidationMessage, setShowMaxAmountValidationMessage] = useState(false);
    const [loading, setLoading] = useState(true);
    const { language, colorScheme } = useAppSelector(appSettingsSelector);
    const [showPersonalData, setShowPersonalData] = useState(true);
    const [country, setCountry] = useState('');
    const [showCountryValidationMessage, setShowCountryValidationMessage] = useState(false);
    const [state, setState] = useState('');
    const { directCardData, billingFields, setBillingFields } = usePaymentProvider1DirectCardData(
        selectedProvider,
        selectedPaymentProvider1Method,
        selectedPaymentProvider1Currency
    );
    const [dobDate, setDobDate] = useState<Date>(null);
    const [showDOBValidationMessage, setShowDOBValidationMessage] = useState(false);
    const [showInvalidDOBValidationMessage, setShowInvalidDOBValidationMessage] = useState(false);

    useEffect(() => {
        if (cardPaymentConfigData === undefined && configData && configData.card) {
            for (let i = 0; i < configData.card.length; i++) {
                let paymentConfig: PaymentConfig = configData.card[i];
                if (paymentConfig.provider === selectedProvider) {
                    setCardPaymentConfigData(paymentConfig);
                    setLoading(false);
                }
            }
        }
    }, [cardPaymentConfigData, configData, selectedProvider]);

    useEffect(() => {
        if (ratesAndCurrencies && selectedPaymentProvider1Currency) {
            for (let i = 0; i < ratesAndCurrencies.rates.length; i++) {
                if (ratesAndCurrencies.rates[i].currency === selectedPaymentProvider1Currency) {
                    setSelectedRate(ratesAndCurrencies.rates[i]);
                    break;
                }
            }
        }
    }, [ratesAndCurrencies, selectedPaymentProvider1Currency]);

    useEffect(() => {
        if (selectedProvider === 'PAYMENT_PROVIDER_1') {
            // ok
        } else {
            return;
        }
        if (directCardData === undefined) {
            setShowPersonalData(true);
            return;
        }
        setCountry(directCardData.customer.country);

        let billingFieldValid = true;
        for (const billingField of directCardData.billingFields) {
            if (!billingField.value) {
                billingFieldValid = false;
                break;
            }
        }

        let hasDobDate = false;
        if (directCardData.customer.dob) {
            const dobDateValue = parseDateServer(directCardData.customer.dob);
            if (dobDateValue) {
                setDobDate(dobDateValue);
                hasDobDate = true;
            }
        }

        if (
            directCardData.customer.dob === '' ||
            !hasDobDate ||
            directCardData.customer.country === '' ||
            !billingFieldValid ||
            (directCardData.customer.state === '' &&
                (directCardData.customer.country === 'US' ||
                    directCardData.customer.country === 'CA' ||
                    directCardData.customer.country === 'AU'))
        ) {
            setShowPersonalData(true);
        } else {
            setShowPersonalData(false);
        }
    }, [directCardData, selectedProvider]);

    function onChangeCountry(text: string) {
        setCountry(text);
        if (text === '') {
            setShowCountryValidationMessage(true);
        } else {
            setShowCountryValidationMessage(false);
        }
    }

    function onChangeBillingField(index: number, text: string) {
        const values = [...billingFields];
        values[index].value = text;
        isValidField(values[index]);
        setBillingFields(values);
    }

    function validateDob(): boolean {
        if (!dobDate) {
            setShowDOBValidationMessage(true);
            setShowInvalidDOBValidationMessage(false);
            return false;
        }

        const now = new Date();
        const nowBefore18Years = new Date(now.getTime());
        nowBefore18Years.setFullYear(nowBefore18Years.getFullYear() - 18);
        if (dobDate.getTime() > nowBefore18Years.getTime()) {
            setShowDOBValidationMessage(false);
            setShowInvalidDOBValidationMessage(true);
            return false;
        }

        setShowDOBValidationMessage(false);
        setShowInvalidDOBValidationMessage(false);
        return true;
    }

    function isValidField(f: PaymentProvider1BillingField): boolean {
        // reset
        f.showValidationEmail = false;
        f.showValidation = false;

        // validate required
        if (!f.value) {
            f.showValidation = true;
            return false;
        }

        // validate email
        if (f.format === 'email') {
            if (!validateEmail(f.value)) {
                f.showValidationEmail = true;
                return false;
            }
        }

        // valid
        return true;
    }

    function validateAmount(vAmount: string): boolean {
        if (vAmount === '') {
            setShowAmountValidationMessage(true);
            return false;
        } else {
            setShowAmountValidationMessage(false);
            if (cardPaymentConfigData) {
                if (Number(vAmount) < cardPaymentConfigData.minAmount) {
                    setShowMinAmountValidationMessage(true);
                    return false;
                } else {
                    setShowMinAmountValidationMessage(false);
                }
                if (Number(vAmount) > cardPaymentConfigData.maxAmount) {
                    setShowMaxAmountValidationMessage(true);
                    return false;
                } else {
                    setShowMaxAmountValidationMessage(false);
                }
            }
        }
        return true;
    }

    function validateForm(): boolean {
        let formValid: boolean = true;

        if (country === '') {
            formValid = false;
            setShowPersonalData(true);
            setShowCountryValidationMessage(true);
        } else {
            setShowCountryValidationMessage(false);
        }

        if (!validateDob()) {
            formValid = false;
            setShowPersonalData(true);
        }

        const values = [...billingFields];
        let billingFieldValid = true;
        for (const billingField of values) {
            if (!isValidField(billingField)) {
                formValid = false;
                billingFieldValid = false;
            }
        }
        if (!billingFieldValid) {
            setBillingFields(values);
        }

        if (!selectedRate || selectedRate.currency === '') {
            console.log('formValid selectedRate');
            formValid = false;
        }
        if (!validateAmount(amount)) {
            console.log('formValid validateAmount');
            formValid = false;
        }
        return formValid;
    }

    async function payDeposit() {
        setPaymentResponseErrorMessage('');
        if (validateForm()) {
            setLoading(true);
            try {
                const customerBillingFields = {};
                for (const billingField of billingFields) {
                    customerBillingFields[billingField.id] = billingField.value;
                }
                const { status, data } = await OrderConvertService.createOrderPaymentProvider1DirectCard({
                    convertId: ratesAndCurrencies ? ratesAndCurrencies.convertId : '',
                    depositAmount: Number(amount),
                    depositCurrency: currency,
                    payAmount: selectedRate.rate * amount,
                    payCurrency: selectedRate.currency,
                    paymentMethod: selectedPaymentProvider1Method,
                    returnErrorUrl:
                        `${config.PLATFORM_BASE_URL}mobileplatform/paymentprovider1/error/index.html` +
                        '?language=' +
                        language +
                        '&theme=' +
                        colorScheme,
                    returnSuccessUrl:
                        `${config.PLATFORM_BASE_URL}mobileplatform/paymentprovider1/success/index.html` +
                        '?language=' +
                        language +
                        '&theme=' +
                        colorScheme,
                    customer: {
                        country: country,
                        state: state,
                        dob: formatDateServer(dobDate),
                        billingFields: customerBillingFields,
                    },
                });
                setLoading(false);
                if (status === 200 && data.status) {
                    changeRedirectUrl(data.returnData.config.url);
                    Linking.openURL(data.returnData.config.url);
                } else {
                    setPaymentResponseErrorMessage('payment-provider1-error');
                }
            } catch (error) {
                setPaymentResponseErrorMessage((error as Error).message || 'Error');
                setLoading(false);
            }
        } else {
            setPaymentResponseErrorMessage('please-fill-all-required-fields');
        }
    }

    function changeRedirectUrl(urlValue: string) {
        console.log('changeRedirectUrl', urlValue);
        const u = new URL(urlValue);
        const searchParams = u.searchParams;
        const entries = searchParams.entries();
        const fields: Field[] = [];
        for (const entry of entries) {
            fields.push({
                name: entry[0],
                value: entry[1],
            });
        }
        u.search = '';
        const urlAction = u.toString();
        console.log('changeRedirectUrl urlAction', urlAction);
        console.log('changeRedirectUrl fields', fields);
        setRedirectUrl(urlAction);
        setRedirectFields(fields);
    }

    function enableDepositButton() {
        if (!confirm) {
            return false;
        }
        return true;
    }

    function checkConfirm() {
        setConfirm(!confirm);
    }

    if (!directCardData) {
        return;
    }

    const countryOptions = countries
        .filter(
            (item) =>
                cardPaymentConfigData?.availableCountries[0] === 'ALL' ||
                cardPaymentConfigData?.availableCountries.find((s) => s === item.value) !== undefined
        )
        .map((c) => ({ value: c.value, label: c.label }));

    const stateOptions = country === 'US' ? usState : country === 'CA' ? caStates : country === 'AU' ? auStates : null;

    return (
        <FlowScreen
            title="card-payment-title"
            goToScreenName="add-money-category"
            setActiveRightTab={setActiveRightTab}
            footer={
                redirectUrl === '' ? (
                    <>
                        <Button
                            title="deposit-button-text"
                            onPress={() => {
                                payDeposit();
                            }}
                            disabled={!enableDepositButton()}
                        />
                        <ErrorMessage text={paymentResponseErrorMessage} visible={paymentResponseErrorMessage !== ''} />
                    </>
                ) : null
            }
        >
            <Loader loading={loading} />
            {redirectUrl === '' ? (
                <>
                    {cardPaymentConfigData ? (
                        <View style={styles.summaryCard}>
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>
                                    {i18n.t('deposit-amount') + ' ( ' + currency + ' )'}
                                </Text>
                                <Text fontType="LATO_BOLD" noTranslate style={styles.summaryValue}>
                                    {formatNumber(amount, 2) + ' ' + currency}
                                </Text>
                            </View>
                            <View style={styles.summaryDivider} />
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>{'deposit-min'}</Text>
                                <Text noTranslate style={styles.summaryMeta}>
                                    {formatNumber(cardPaymentConfigData.minAmount, 2) + ' ' + currency}
                                </Text>
                            </View>
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>{'deposit-max'}</Text>
                                <Text noTranslate style={styles.summaryMeta}>
                                    {formatNumber(cardPaymentConfigData.maxAmount, 2) + ' ' + currency}
                                </Text>
                            </View>
                            {selectedRate && selectedRate.currency ? (
                                <>
                                    <View style={styles.summaryDivider} />
                                    <View style={styles.summaryRow}>
                                        <Text style={styles.summaryLabel}>{'currency-convertor'}</Text>
                                        {/*
                                         * Quote off selectedRate — the same rate payDeposit sends as
                                         * payAmount. This used to read rates[selectedCurrencyIndex],
                                         * an index nothing ever moved off 0, so picking any currency
                                         * other than the first showed a figure that did not match the
                                         * amount actually charged.
                                         */}
                                        <Text fontType="LATO_BOLD" noTranslate style={styles.summaryValue}>
                                            {formatNumber(selectedRate.rate * amount, 2) + ' ' + selectedRate.currency}
                                        </Text>
                                    </View>
                                    {currency !== selectedRate.currency ? (
                                        <View style={styles.summaryRow}>
                                            <Text noTranslate style={styles.summaryMeta}>
                                                {'1 ' +
                                                    currency +
                                                    ' = ' +
                                                    selectedRate.rate +
                                                    ' ' +
                                                    selectedRate.currency}
                                            </Text>
                                        </View>
                                    ) : null}
                                </>
                            ) : null}
                            <ErrorMessage text={'missing-amount-error'} visible={showAmountValidationMessage} />
                            <ErrorMessage text={'amount-max-error'} visible={showMaxAmountValidationMessage} />
                            <ErrorMessage text={'amount-min-error'} visible={showMinAmountValidationMessage} />
                        </View>
                    ) : null}

                    <Pressable
                        style={styles.consentRow}
                        onPress={() => {
                            setShowPersonalData(!showPersonalData);
                        }}
                    >
                        <Checkbox
                            checked={!showPersonalData}
                            setChecked={() => {
                                setShowPersonalData(!showPersonalData);
                            }}
                        />
                        <Text style={styles.consentText}>{'use-personal-information-from-registration'}</Text>
                        {config.USE_ADDRESS_LINK ? (
                            <Pressable
                                style={styles.helpIconContainer}
                                onPress={() => Linking.openURL(`${config.USE_ADDRESS_LINK}`)}
                            >
                                <FontAwesome name={'question'} size={scaleSize(8)} style={styles.helpIcon} />
                            </Pressable>
                        ) : null}
                    </Pressable>

                    {showPersonalData ? (
                        <View style={styles.formSection}>
                            <FormField
                                label="country"
                                errors={[{ text: 'please-select-country', visible: showCountryValidationMessage }]}
                            >
                                <Select
                                    value={country}
                                    options={countryOptions}
                                    onChange={onChangeCountry}
                                    placeholder="please-select-country"
                                    title="country"
                                    hasError={showCountryValidationMessage}
                                />
                            </FormField>

                            {stateOptions ? (
                                <FormField label="state">
                                    <Select
                                        value={state}
                                        options={stateOptions.map((s) => ({ value: s.value, label: s.label }))}
                                        onChange={setState}
                                        placeholder="state"
                                        title="state"
                                    />
                                </FormField>
                            ) : null}

                            <FormField
                                label="date-of-birth"
                                hint={dateFormatHint()}
                                errors={[
                                    { text: 'please-enter-date-ofbirth', visible: showDOBValidationMessage },
                                    { text: 'invalid-birth-date', visible: showInvalidDOBValidationMessage },
                                ]}
                            >
                                <DateField
                                    value={dobDate}
                                    onChange={(value) => {
                                        setDobDate(value);
                                    }}
                                    hasError={showDOBValidationMessage || showInvalidDOBValidationMessage}
                                />
                            </FormField>

                            {billingFields &&
                                billingFields.map((_, index) => (
                                    <BillingFieldComponent
                                        key={`bf-${index}`}
                                        fields={billingFields}
                                        idx={index}
                                        onChangeBillingField={onChangeBillingField}
                                    />
                                ))}
                        </View>
                    ) : null}

                    <Pressable style={styles.consentRow} onPress={checkConfirm}>
                        <Checkbox checked={confirm} setChecked={checkConfirm} />
                        <Text style={styles.consentText}>{'deposit-confirm'}</Text>
                    </Pressable>
                </>
            ) : (
                <>
                    <Text style={styles.labelText} noTranslate={true}>
                        If you are not automatically redirected to payment page, please click the button "Continue"
                        below.
                    </Text>
                    <form action={redirectUrl} method={'GET'} target={'_blank'}>
                        {redirectFields.map((item) => (
                            <input key={item.name} type="hidden" name={item.name} value={item.value} />
                        ))}
                        <button type="submit" style={styles.buttonStyle}>
                            CONTINUE
                        </button>
                    </form>
                    <Text style={[styles.labelText, styles.or]}>{'or-text'}</Text>
                    <Button
                        title="back"
                        onPress={() => {
                            setActiveRightTab('add-money-category');
                        }}
                    />
                </>
            )}

            <View style={styles.supportContainer}>
                <Text style={styles.supportText}>{'customer-service-part1'}</Text>
                <View style={styles.supportRow}>
                    <Text style={styles.supportText}>{'customer-service-part2'}</Text>
                    {config.CUSTOMER_SERVICE_LINK ? (
                        <Pressable onPress={() => Linking.openURL(`${config.CUSTOMER_SERVICE_LINK}`)}>
                            <Text style={styles.supportLink}>{'customer-service'}</Text>
                        </Pressable>
                    ) : null}
                </View>
            </View>
            <CopyRight />
        </FlowScreen>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        buttonStyle: {
            fontWeight: 'bold',
            fontSize: FONT_SIZE_L,
            color: theme.white,
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            borderRadius: scaleSize(5),
            width: '100%',
            backgroundColor: theme.text_upPrice,
        },
        or: { textAlign: 'center' },
        summaryCard: {
            borderRadius: scaleSize(8),
            borderWidth: 1,
            borderColor: theme.border_2,
            backgroundColor: theme.backgroundSecondary,
            padding: scaleSize(12),
            marginBottom: scaleSize(20),
        },
        summaryRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: scaleSize(3),
        },
        summaryDivider: {
            height: StyleSheet.hairlineWidth,
            backgroundColor: theme.border_2,
            marginVertical: scaleSize(8),
        },
        summaryLabel: { fontSize: FONT_SIZE_S, color: theme.textSecondary, flexShrink: 1 },
        summaryValue: { fontSize: FONT_SIZE_M, color: theme.text },
        summaryMeta: { fontSize: FONT_SIZE_S, color: theme.text_positionsCount },
        formSection: { paddingTop: scaleSize(4) },
        consentRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: scaleSize(14),
        },
        consentText: {
            flex: 1,
            fontSize: FONT_SIZE_S,
            color: theme.text_positionsCount,
            marginLeft: scaleSize(10),
        },
        helpIcon: { color: theme.white },
        helpIconContainer: {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.tabIconSelected,
            width: scaleSize(15),
            height: scaleSize(15),
            borderRadius: scaleSize(12.5),
            marginLeft: scaleSize(8),
        },
        labelText: { fontSize: FONT_SIZE_S, color: theme.text_symbol, paddingVertical: scaleSize(10) },
        supportContainer: { paddingTop: scaleSize(24) },
        supportRow: { flexDirection: 'row', flexWrap: 'wrap' },
        supportText: { fontSize: FONT_SIZE_XS, color: theme.text_positionsCount },
        supportLink: { color: theme.switch_trackOn, fontSize: FONT_SIZE_XS },
    });
